/**
 * Project Bobcat — turning a Knowledge Agent's knowledge search off, and back
 * on once a knowledge base exists.
 *
 * Skip: `planSkipKnowledgeCorpus` (skip-knowledge-corpus.ts) decides what
 * goes; this turns the plan into the solution's next rows AND records what
 * it changed, as `settings.skippedKnowledgeSearch` on the step the lookup
 * now routes to (the answer step). The record is what makes the restore
 * exact: the removed rows as they were, and every field the skip rewrote.
 *
 * Restore: puts the recorded rows back in front of the answer step (inside
 * its group) and reverts only the fields the skip rewrote — and only where
 * they still hold what the skip wrote, so a later edit is never undone.
 * Solutions skipped before the record existed get the stock template's
 * lookup instead (see `restoreFromTemplate`).
 *
 * Pure — the store applies the result.
 */
import {
  RAG_SEARCH_COMMAND,
  isUnprovisionedRagNode,
  parseParam,
  planSkipKnowledgeCorpus,
  type SkipCorpusRow,
} from './skip-knowledge-corpus';

type Settings = Record<string, unknown> & {
  groupId?: string | null;
  subRows?: string[];
  skippedKnowledgeSearch?: SkippedKnowledgeSearch;
};

/** The row shape these transforms read and write — `TableRow` fits it. */
export type KnowledgeRow = SkipCorpusRow & {
  order?: number;
  settings?: Settings;
  [key: string]: unknown;
};

type RewiredField = 'whatsNext' | 'nextNodes' | 'parameterInput';

export interface SkippedKnowledgeSearch {
  version: 1;
  /** The lookup rows as they were, in order: query writer(s), then search. */
  removed: KnowledgeRow[];
  /** Fields the skip rewrote, with the value before and after. */
  rewired: {
    rowId: string;
    before: Partial<Record<RewiredField, unknown>>;
    after: Partial<Record<RewiredField, unknown>>;
  }[];
}

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

const same = (a: unknown, b: unknown) =>
  JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/** A RAG lookup is still in the flow, waiting on a corpus that never came. */
export const hasUnprovisionedKnowledgeSearch = (rows: SkipCorpusRow[]) =>
  rows.some(isUnprovisionedRagNode);

/**
 * Renumbers `order` to follow the array's sequence, touching only rows whose
 * position moved relative to their neighbours' order values.
 */
const withSequentialOrder = <R extends KnowledgeRow>(rows: R[]): R[] => {
  const inOrder = rows.every(
    (row, i) =>
      typeof row.order === 'number' &&
      (i === 0 || (rows[i - 1].order as number) < row.order)
  );
  if (inOrder) return rows;
  return rows.map((row, i) =>
    row.order === i + 1 ? row : ({ ...row, order: i + 1 } as R)
  );
};

const sortedByOrder = <R extends KnowledgeRow>(rows: R[]) =>
  rows
    .map((row, i) => ({ row, i }))
    .sort((a, b) => (a.row.order ?? 0) - (b.row.order ?? 0) || a.i - b.i)
    .map(({ row }) => row);

/**
 * The rows after dropping the unprovisioned RAG lookup, with the change
 * recorded on the answer step. `null` when there is nothing to skip.
 */
export const skipKnowledgeSearchRows = <R extends KnowledgeRow>(
  rows: R[]
): R[] | null => {
  const plan = planSkipKnowledgeCorpus(rows);
  if (!plan) return null;
  const byId = new Map(rows.map((row) => [row.id, row]));
  const removed = new Set(plan.removeRowIds);
  const updates = new Map(plan.rowUpdates.map((u) => [u.rowId, u.updates]));

  // One record per lookup, kept on the step it now routes to. The rewired
  // fields go on the first record: they're reverted together either way.
  const markers = new Map<number, SkippedKnowledgeSearch>();
  plan.redirects.forEach(({ removedRowIds, targetNodeNumber }, index) => {
    const marker = markers.get(targetNodeNumber) ?? {
      version: 1 as const,
      removed: [],
      rewired: [],
    };
    removedRowIds.forEach((id) => {
      const row = byId.get(id);
      if (row) marker.removed.push(clone(row));
    });
    if (index === 0) {
      plan.rowUpdates.forEach(({ rowId, updates: after }) => {
        const original = byId.get(rowId);
        if (!original) return;
        const before: SkippedKnowledgeSearch['rewired'][number]['before'] = {};
        (Object.keys(after) as RewiredField[]).forEach((field) => {
          before[field] = clone(original[field] ?? null);
        });
        marker.rewired.push({ rowId, before, after: clone(after) });
      });
    }
    markers.set(targetNodeNumber, marker);
  });

  return rows
    .filter((row) => !removed.has(row.id))
    .map((row) => {
      let next = row;
      const rowUpdates = updates.get(row.id);
      if (rowUpdates) next = { ...next, ...rowUpdates };
      const subRows = next.settings?.subRows;
      if (subRows?.some((id) => removed.has(id))) {
        next = {
          ...next,
          settings: {
            ...next.settings,
            subRows: subRows.filter((id) => !removed.has(id)),
          },
        };
      }
      const marker =
        row.nodeNumber === undefined ? undefined : markers.get(row.nodeNumber);
      if (marker) {
        next = {
          ...next,
          settings: { ...next.settings, skippedKnowledgeSearch: marker },
        };
      }
      return next;
    });
};

export interface RestoreKnowledgeSearchResult<R> {
  rows: R[];
  restoredNodeNumbers: number[];
  /** Things the restore could not put back, in words for the user. */
  notes: string[];
  /** True when the stock template stood in for a missing record. */
  usedTemplate: boolean;
}

/** Inserts `inserted` right before `host`, in the array and in its group. */
const insertBefore = <R extends KnowledgeRow>(
  rows: R[],
  host: R,
  inserted: R[]
): R[] => {
  const groupId = host.settings?.groupId ?? null;
  const placed = inserted.map(
    (row) =>
      ({
        ...row,
        settings: { ...row.settings, groupId },
      }) as R
  );
  const ids = new Set(placed.map((row) => row.id));
  const sorted = sortedByOrder(rows);
  const at = sorted.findIndex((row) => row.id === host.id);
  const sequence = [...sorted.slice(0, at), ...placed, ...sorted.slice(at)];
  const withGroup = sequence.map((row) => {
    if (!groupId || row.id !== groupId) return row;
    const subRows = (row.settings?.subRows ?? []).filter((id) => !ids.has(id));
    const hostAt = subRows.indexOf(host.id);
    const nextSubRows =
      hostAt === -1
        ? [...subRows, ...placed.map((r) => r.id)]
        : [
            ...subRows.slice(0, hostAt),
            ...placed.map((r) => r.id),
            ...subRows.slice(hostAt),
          ];
    return { ...row, settings: { ...row.settings, subRows: nextSubRows } };
  });
  return withSequentialOrder(withGroup);
};

const SUPERVISOR_ROUTING_COMMANDS = new Set([
  'SupervisorRouting',
  'SysSupervisorRouting',
]);

/**
 * The stock Knowledge Agent lookup (Supervisor + Knowledge template, as the
 * backend builds it — the same rows Landing Check and the Protolab export
 * carry). Only used for a solution whose lookup was dropped before the skip
 * recorded what it removed.
 */
export const TEMPLATE_QUERY_NODE = 7200;
export const TEMPLATE_RAG_NODE = 7210;

const templateLookupRows = (
  answerNodeNumber: number,
  noMatchNodeNumber: number,
  errorNodeNumber: number
): Omit<KnowledgeRow, 'id'>[] => [
  {
    nodeNumber: TEMPLATE_QUERY_NODE,
    nodeType: 'a',
    nodeName:
      'Knowledge Agent Step 1 (when classified as other_in_scope ONLY): Goes to this script GetGeminiCompletionSimpleV2 to create RAG Search JSON Query',
    message: 'LLM call to determine our RAG search query in JSON',
    command: 'GetGeminiCompletionSimpleV2',
    // The Output column is an array (`text[]`) — a bare string is rejected
    // by the database ("malformed array literal"), failing the whole save.
    output: ['success'],
    decisionVariable: 'success',
    variables: ['UTTERANCE', 'TRANSCRIPT_FOR_GEMINI', 'RAG_SEARCH_QUERY'],
    parameterInput: {
      chat_context_setting:
        "You are a retrieval engineer. Your job is to turn a conversation transcript and the user's latest utterance into search queries that maximize recall without tanking precision.",
      model: 'gemini-2.5-flash',
      parameters: {
        max_output_tokens: 8192,
        temperature: 0.1,
        thinking_config: { thinking_budget: 0 },
        top_k: 20,
        top_p: 0.9,
      },
      safety_settings: [
        'HARM_CATEGORY_HARASSMENT',
        'HARM_CATEGORY_SEXUALLY_EXPLICIT',
        'HARM_CATEGORY_DANGEROUS_CONTENT',
        'HARM_CATEGORY_HATE_SPEECH',
      ].map((category) => ({ category, threshold: 'BLOCK_NONE' })),
      save_llm_response_as: 'RAG_SEARCH_QUERY',
      user_utterance:
        'CONTEXT\n------\nFull transcript (oldest → newest):\n{TRANSCRIPT_FOR_GEMINI}\n\nLast user utterance (query to satisfy):\n{UTTERANCE}',
    },
    whatsNext: [
      `true~${TEMPLATE_RAG_NODE}`,
      `false~${errorNodeNumber}`,
      `error~${errorNodeNumber}`,
    ],
  },
  {
    nodeNumber: TEMPLATE_RAG_NODE,
    nodeType: 'a',
    nodeName:
      'Knowledge Agent Step 2 (when classified as other_in_scope ONLY): GetRAGSearchResponse',
    message: 'Hit RAG engine and get response with matched documents',
    command: RAG_SEARCH_COMMAND,
    // The Output column is an array (`text[]`) — a bare string is rejected
    // by the database ("malformed array literal"), failing the whole save.
    output: ['success'],
    decisionVariable: 'success',
    variables: ['RAG_SEARCH_QUERY', 'CONCATENATED_KB_BODY'],
    parameterInput: {
      input_var: 'RAG_SEARCH_QUERY',
      live_corpus_id: '',
      sandbox_corpus_id: '',
      save_output_as: 'CONCATENATED_KB_BODY',
    },
    whatsNext: [
      `true~${answerNodeNumber}`,
      `false~${noMatchNodeNumber}`,
      `error~${errorNodeNumber}`,
    ],
  },
];

const numberFrom = (value: unknown): number | null => {
  const n = typeof value === 'string' ? Number(value) : value;
  return typeof n === 'number' && Number.isFinite(n) ? n : null;
};

/**
 * No record, no lookup — rebuild the stock one: the Supervisor's
 * `other_in_scope` destination (the only route into the lookup in the stock
 * template; `out_of_scope`/`chit_chat`/`profanity` always went straight to
 * the answer step) is taken as the answer step, and routed back through the
 * rebuilt lookup.
 */
const restoreFromTemplate = <R extends KnowledgeRow>(
  rows: R[],
  newRow: (fields: Omit<KnowledgeRow, 'id'>, template: R) => R
): RestoreKnowledgeSearchResult<R> | null => {
  if (rows.some((row) => row.command === RAG_SEARCH_COMMAND)) return null;
  const supervisor = rows.find(
    (row) =>
      typeof row.command === 'string' &&
      SUPERVISOR_ROUTING_COMMANDS.has(row.command)
  );
  const supervisorParam = supervisor
    ? parseParam(supervisor.parameterInput)
    : null;
  const destinations = supervisorParam?.destination_nodes as
    | Record<string, unknown>
    | undefined;
  const answerNumber = numberFrom(destinations?.other_in_scope);
  const answer = rows.find((row) => row.nodeNumber === answerNumber);
  if (!supervisor || !destinations || answerNumber === null || !answer) {
    return null;
  }
  const taken = [TEMPLATE_QUERY_NODE, TEMPLATE_RAG_NODE].filter((n) =>
    rows.some((row) => row.nodeNumber === n)
  );
  if (taken.length) {
    return {
      rows,
      restoredNodeNumbers: [],
      notes: [
        `Node ${taken.join(' and ')} is already used, so the knowledge search could not be added back automatically.`,
      ],
      usedTemplate: true,
    };
  }
  const exists = (n: number | null) =>
    n !== null && rows.some((row) => row.nodeNumber === n);
  const liveAgent = numberFrom(destinations.live_agent);
  const errorDest = numberFrom(destinations.error);
  const noMatch = exists(liveAgent) ? liveAgent! : answerNumber;
  const errorNode = exists(99990)
    ? 99990
    : exists(errorDest)
      ? errorDest!
      : noMatch;

  const lookup = templateLookupRows(answerNumber, noMatch, errorNode).map(
    (fields) => newRow(fields, answer)
  );
  const rewiredSupervisor = {
    ...supervisor,
    parameterInput: (() => {
      const next = {
        ...supervisorParam,
        destination_nodes: {
          ...destinations,
          other_in_scope: TEMPLATE_QUERY_NODE,
        },
      };
      return typeof supervisor.parameterInput === 'string'
        ? JSON.stringify(next)
        : next;
    })(),
  } as R;
  const swapped = rows.map((row) =>
    row.id === supervisor.id ? rewiredSupervisor : row
  );
  return {
    rows: insertBefore(swapped, answer, lookup),
    restoredNodeNumbers: [TEMPLATE_QUERY_NODE, TEMPLATE_RAG_NODE],
    notes: [],
    usedTemplate: true,
  };
};

/**
 * The rows with the knowledge search put back. `null` when there is nothing
 * to restore (a lookup is already there, or no record and no template fit).
 * `newRow` builds a fresh row for the template fallback (ids, version,
 * defaults) — the store supplies it.
 */
export const restoreKnowledgeSearchRows = <R extends KnowledgeRow>(
  rows: R[],
  newRow: (fields: Omit<KnowledgeRow, 'id'>, template: R) => R
): RestoreKnowledgeSearchResult<R> | null => {
  const hosts = rows.filter((row) => row.settings?.skippedKnowledgeSearch);
  if (!hosts.length) return restoreFromTemplate(rows, newRow);

  let next = rows;
  const restoredNodeNumbers: number[] = [];
  const notes: string[] = [];

  for (const host of hosts) {
    const marker = host.settings!.skippedKnowledgeSearch!;
    const { skippedKnowledgeSearch: _drop, ...settings } = host.settings!;
    const clearedHost = { ...host, settings } as R;
    next = next.map((row) => (row.id === host.id ? clearedHost : row));

    const clash = marker.removed.filter((removed) =>
      next.some(
        (row) =>
          row.id === removed.id ||
          (row.nodeNumber !== undefined &&
            row.nodeNumber === removed.nodeNumber)
      )
    );
    if (clash.length) {
      notes.push(
        `Node ${clash.map((r) => r.nodeNumber).join(' and ')} already exists, so that part of the knowledge search was not added back.`
      );
    }
    const toInsert = marker.removed
      .filter((removed) => !clash.includes(removed))
      .map((removed) => clone(removed) as R);
    if (toInsert.length) {
      next = insertBefore(next, clearedHost, toInsert);
      restoredNodeNumbers.push(
        ...toInsert
          .map((row) => row.nodeNumber)
          .filter((n): n is number => n !== undefined)
      );
    }

    for (const { rowId, before, after } of marker.rewired) {
      const row = next.find((r) => r.id === rowId);
      if (!row) continue;
      const revert: Partial<Record<RewiredField, unknown>> = {};
      (Object.keys(after) as RewiredField[]).forEach((field) => {
        if (same(row[field], after[field])) revert[field] = before[field];
        else
          notes.push(
            `Node ${row.nodeNumber}'s routing changed after the search was skipped, so it was left as is — point it at the knowledge search yourself if needed.`
          );
      });
      if (Object.keys(revert).length) {
        next = next.map((r) =>
          r.id === rowId ? ({ ...r, ...revert } as R) : r
        );
      }
    }
  }

  return {
    rows: next,
    restoredNodeNumbers,
    notes,
    usedTemplate: false,
  };
};

/** Whether a restore would do anything for these rows. */
export const canRestoreKnowledgeSearch = <R extends KnowledgeRow>(
  rows: R[]
): boolean =>
  rows.some((row) => row.settings?.skippedKnowledgeSearch) ||
  !!restoreFromTemplate(rows, (fields) => ({ ...fields, id: '' }) as R)
    ?.restoredNodeNumbers.length;
